import { useState, useEffect } from "react";
import API from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import DomainSetupWizard from "../components/DomainSetupWizard";
import { EmailBrandingCard } from "../components/EmailBrandingCard";
import { PdfBrandingCard } from "../components/PdfBrandingCard";
import { LoginLinkCard } from "../components/LoginLinkCard";
import { Palette, Eye, FloppyDisk, Crown, Lock, Globe, CheckCircle, ArrowClockwise, UploadSimple, Spinner } from "@phosphor-icons/react";

const COLOR_PRESETS = [
  { label: "Blue", value: "#1D4ED8" },
  { label: "Indigo", value: "#4F46E5" },
  { label: "Purple", value: "#7C3AED" },
  { label: "Rose", value: "#BE185D" },
  { label: "Red", value: "#DC2626" },
  { label: "Orange", value: "#EA580C" },
  { label: "Green", value: "#059669" },
  { label: "Teal", value: "#0D9488" },
  { label: "Cyan", value: "#0891B2" },
  { label: "Emerald", value: "#10B981" },
];

export default function BrandingSettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1D4ED8");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [fontFamily, setFontFamily] = useState("");
  const [hidePlatformBranding, setHidePlatformBranding] = useState(false);
  const [emailFooter, setEmailFooter] = useState("");
  const [tagline, setTagline] = useState("");
  const [footerText, setFooterText] = useState("");
  const [pdfLogoUrl, setPdfLogoUrl] = useState("");
  const [uploadingPdfLogo, setUploadingPdfLogo] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  // Custom domain
  const [customDomain, setCustomDomain] = useState("");
  const [domainVerified, setDomainVerified] = useState(false);
  const [domainInfo, setDomainInfo] = useState(null);
  const [domainSaving, setDomainSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const feats = Array.isArray(tenant?.features) ? tenant.features : null;
  const isLicence = tenant?.plan === "one_time_licence" || (feats ? feats.includes("white_label") : false);
  const isEnterprise = tenant?.plan === "one_time_licence" || (feats ? feats.includes("custom_domain") : tenant?.plan === "enterprise");

  useEffect(() => {
    API.get("/tenant/current")
      .then(({ data }) => {
        setTenant(data);
        const b = data.branding || {};
        setCompanyName(b.company_name || data.name || "");
        setLogoUrl(b.logo_url || "");
        setPrimaryColor(b.primary_color || "#1D4ED8");
        setFaviconUrl(b.favicon_url || "");
        setFontFamily(b.font_family || "");
        setHidePlatformBranding(b.hide_platform_branding || false);
        setEmailFooter(b.email_footer || "");
        setTagline(b.tagline || "");
        setFooterText(b.footer_text || "");
        setPdfLogoUrl(b.pdf_logo_url || "");
      })
      .catch(() => toast.error("Failed to load branding"))
      .finally(() => setLoading(false));
    // Fetch custom domain
    API.get("/tenant/custom-domain").then(({ data }) => {
      setCustomDomain(data.custom_domain || "");
      setDomainVerified(data.domain_verified || false);
      setDomainInfo(data);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: companyName,
        logo_url: logoUrl,
        primary_color: primaryColor,
        favicon_url: faviconUrl,
        font_family: fontFamily,
        hide_platform_branding: hidePlatformBranding,
        email_footer: emailFooter,
        tagline,
        footer_text: footerText,
        pdf_logo_url: pdfLogoUrl.split("?")[0],
      };
      await API.put("/tenant/branding", payload);
      setSavedAt(Date.now());
      toast.success("Branding updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    setDomainSaving(true);
    try {
      const { data } = await API.put("/tenant/custom-domain", { custom_domain: customDomain });
      setDomainVerified(data.domain_verified);
      setDomainInfo((d) => ({ ...(d || {}), ...data, domain_verified_at: null, domain_last_checked_at: null }));
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save domain");
    } finally {
      setDomainSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifying(true);
    try {
      const { data } = await API.post("/tenant/verify-domain");
      setDomainVerified(data.verified);
      setDomainInfo((d) => ({ ...(d || {}), txt_host: data.txt_host, txt_value: data.txt_value, domain_verified_at: data.verified_at, domain_last_checked_at: data.checked_at }));
      (data.verified ? toast.success : toast.warning)(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleUpload = async (file, assetType) => {
    if (!file) return;
    const setter = assetType === "logo" ? setUploadingLogo : assetType === "pdf_logo" ? setUploadingPdfLogo : setUploadingFavicon;
    setter(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", assetType);
      const { data } = await API.post("/tenant/branding/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (assetType === "logo") setLogoUrl(data.url);
      else if (assetType === "pdf_logo") setPdfLogoUrl(`${data.url}?v=${Date.now()}`);
      else setFaviconUrl(data.url);
      toast.success(`${assetType === "logo" ? "Logo" : assetType === "pdf_logo" ? "PDF logo" : "Favicon"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setter(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading branding...</div>;

  return (
    <div data-testid="branding-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-pink-600 to-violet-600 flex items-center justify-center">
            <Palette size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Branding Settings</h1>
            <p className="text-xs text-muted-foreground">
              Customise your workspace appearance
              {!isLicence && <span className="ml-1">&middot; <Link to="/platform/billing?add=white_label" className="text-primary hover:underline" data-testid="branding-add-white-label-link">Add White-label for full customisation</Link></span>}
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
          <FloppyDisk size={14} className="mr-1.5" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Branding */}
          <div className="border border-border rounded-lg p-5 bg-card" data-testid="branding-basic-section">
            <h2 className="text-sm font-bold font-[Chivo] mb-4">Basic Branding</h2>

            <div className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company"
                  className="mt-1"
                  data-testid="branding-company-name"
                />
              </div>

              <div>
                <Label>Logo</Label>
                <div className="flex items-center gap-3 mt-1">
                  {logoUrl ? (
                    <div className="h-14 w-20 rounded border border-border flex items-center justify-center bg-muted overflow-hidden shrink-0">
                      <img src={logoUrl} alt="Logo" className="h-12 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                  ) : (
                    <div className="h-14 w-20 rounded border-2 border-dashed border-border flex items-center justify-center bg-muted/30 shrink-0">
                      <UploadSimple size={18} className="text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="flex-1 space-y-1.5">
                    <label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={(e) => handleUpload(e.target.files?.[0], "logo")}
                        disabled={uploadingLogo}
                        data-testid="branding-logo-upload-input"
                      />
                      <Button variant="outline" size="sm" asChild disabled={uploadingLogo}>
                        <span className="cursor-pointer" data-testid="branding-logo-upload-btn">
                          {uploadingLogo ? (
                            <><Spinner size={14} className="mr-1.5 animate-spin" />Uploading...</>
                          ) : (
                            <><UploadSimple size={14} className="mr-1.5" />Upload Logo</>
                          )}
                        </span>
                      </Button>
                    </label>
                    <p className="text-[10px] text-muted-foreground">
                      PNG, SVG, JPEG or WebP. Max 5MB. In e-mails it's fitted inside 300 × 110 px —{" "}
                      <a href="#email-branding" className="text-primary hover:underline" data-testid="branding-logo-email-preview-link">see how your e-mails will look</a>.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Primary Colour</Label>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex gap-1.5 flex-wrap">
                    {COLOR_PRESETS.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setPrimaryColor(c.value)}
                        title={c.label}
                        className={`h-7 w-7 rounded-full border-2 transition-all hover:scale-110 ${primaryColor === c.value ? "border-foreground scale-110 shadow-md" : "border-transparent"}`}
                        style={{ backgroundColor: c.value }}
                        data-testid={`branding-color-${c.label.toLowerCase()}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-7 w-9 p-0 border-0 cursor-pointer rounded"
                      data-testid="branding-color-picker"
                    />
                    <Input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-24 h-7 text-xs font-mono"
                      data-testid="branding-color-hex"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* White-Label (Licence only) */}
          <div className={`border rounded-lg p-5 ${isLicence ? "border-border bg-card" : "border-border/50 bg-muted/20"}`} data-testid="branding-whitelabel-section">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-sm font-bold font-[Chivo]">White-Label Settings</h2>
              {isLicence ? (
                <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Add-on active</Badge>
              ) : (
                <Badge variant="outline" className="text-[8px] gap-1"><Lock size={8} /> White-label add-on</Badge>
              )}
            </div>

            <div className={`space-y-4 ${!isLicence ? "opacity-50 pointer-events-none" : ""}`}>
              <div>
                <Label>Favicon</Label>
                <div className="flex items-center gap-3 mt-1">
                  {faviconUrl ? (
                    <div className="h-10 w-10 rounded border border-border flex items-center justify-center bg-muted overflow-hidden shrink-0">
                      <img src={faviconUrl} alt="Favicon" className="h-8 w-8 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                  ) : (
                    <div className="h-10 w-10 rounded border-2 border-dashed border-border flex items-center justify-center bg-muted/30 shrink-0">
                      <UploadSimple size={14} className="text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="flex-1 space-y-1.5">
                    <label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon,image/vnd.microsoft.icon"
                        className="hidden"
                        onChange={(e) => handleUpload(e.target.files?.[0], "favicon")}
                        disabled={!isLicence || uploadingFavicon}
                        data-testid="branding-favicon-upload-input"
                      />
                      <Button variant="outline" size="sm" asChild disabled={!isLicence || uploadingFavicon}>
                        <span className="cursor-pointer" data-testid="branding-favicon-upload-btn">
                          {uploadingFavicon ? (
                            <><Spinner size={14} className="mr-1.5 animate-spin" />Uploading...</>
                          ) : (
                            <><UploadSimple size={14} className="mr-1.5" />Upload Favicon</>
                          )}
                        </span>
                      </Button>
                    </label>
                    <p className="text-[10px] text-muted-foreground">ICO, PNG, SVG or WebP. Max 5MB.</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Custom Font Family</Label>
                <Input
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  placeholder="Inter, Poppins, etc."
                  className="mt-1"
                  disabled={!isLicence}
                  data-testid="branding-font-family"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Google Fonts name — will be applied across your workspace</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Hide Platform Branding</Label>
                  <p className="text-[10px] text-muted-foreground">Remove "Powered by Fixlr" from your workspace</p>
                </div>
                <Switch
                  checked={hidePlatformBranding}
                  onCheckedChange={setHidePlatformBranding}
                  disabled={!isLicence}
                  data-testid="branding-hide-platform"
                />
              </div>
            </div>

            {!isLicence && (
              <div className="mt-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2">
                  <Crown size={14} className="text-amber-600 shrink-0" weight="fill" />
                  <p className="text-[11px] text-amber-700 dark:text-amber-400">
                    Add the <Link to="/platform/billing?add=white_label" className="underline font-semibold">White-label add-on</Link> from Billing to unlock custom favicon, fonts, and removal of "Powered by Fixlr".
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Login link */}
          <LoginLinkCard companyName={companyName} />

          {/* Custom Domain Section */}
          <div className={`border rounded-lg p-5 ${isEnterprise ? "border-border bg-card" : "border-border/50 bg-muted/20"}`} data-testid="branding-custom-domain-section">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={16} className="text-blue-500" weight="duotone" />
              <h2 className="text-sm font-bold font-[Chivo]">Custom Domain</h2>
              {isEnterprise ? (
                <Badge className="text-[8px] bg-blue-100 text-blue-700">Available</Badge>
              ) : (
                <Badge variant="outline" className="text-[8px] gap-1"><Lock size={8} /> Custom Domain add-on</Badge>
              )}
            </div>

            <div className={`space-y-4 ${!isEnterprise ? "opacity-50 pointer-events-none" : ""}`}>
              <div>
                <Label>Custom Domain</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value.toLowerCase())}
                    placeholder="app.yourcompany.com"
                    className="flex-1"
                    disabled={!isEnterprise}
                    data-testid="branding-custom-domain"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveDomain}
                    disabled={!isEnterprise || domainSaving}
                    data-testid="save-domain-btn"
                  >
                    {domainSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>

              {customDomain && (
                <DomainSetupWizard domain={customDomain} info={domainInfo} verified={domainVerified} verifying={verifying} onVerify={handleVerifyDomain} />
              )}
            </div>

            {!isEnterprise && (
              <div className="mt-4 p-3 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <Globe size={14} className="text-blue-600 shrink-0" weight="fill" />
                  <p className="text-[11px] text-blue-700 dark:text-blue-400">
                    Add the <Link to="/platform/billing?add=custom_domain" className="underline font-semibold">Custom Domain add-on</Link> from Billing to serve your portal from your own domain.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Column */}
        <div className="space-y-4">
          <div className="border border-border rounded-lg overflow-hidden sticky top-6" data-testid="branding-live-preview">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center gap-2">
              <Eye size={14} className="text-muted-foreground" />
              <span className="text-xs font-semibold font-[Chivo]">Live Preview</span>
            </div>

            {/* Sidebar preview */}
            <div className="p-4 bg-card">
              <div className="border border-border rounded-lg overflow-hidden">
                {/* Fake sidebar header */}
                <div className="h-10 border-b border-border px-3 flex items-center gap-2 bg-card">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="h-6 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="h-6 w-6 rounded flex items-center justify-center text-white text-[9px] font-bold" style={{ backgroundColor: primaryColor }}>
                      {companyName?.charAt(0)?.toUpperCase() || "C"}
                    </div>
                  )}
                  <span className="text-xs font-bold truncate" style={{ fontFamily: fontFamily || "inherit" }}>
                    {companyName || "Your Company"}
                  </span>
                </div>
                {/* Fake nav items */}
                <div className="p-2 space-y-0.5">
                  <div className="h-7 rounded-md flex items-center px-2 text-white text-[10px] font-medium" style={{ backgroundColor: primaryColor }}>
                    Dashboard
                  </div>
                  {["Projects", "Jobs", "Suppliers"].map((item) => (
                    <div key={item} className="h-7 rounded-md flex items-center px-2 text-[10px] text-muted-foreground hover:bg-muted">
                      {item}
                    </div>
                  ))}
                </div>
                {/* Fake content */}
                <div className="border-t border-border p-3">
                  <div className="h-3 w-24 rounded bg-muted mb-2" />
                  <div className="h-2 w-full rounded bg-muted/60 mb-1.5" />
                  <div className="h-2 w-3/4 rounded bg-muted/60 mb-3" />
                  <div className="h-6 w-20 rounded text-white text-[8px] flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                    Action
                  </div>
                </div>
                {!isLicence && (
                  <div className="border-t border-border px-3 py-2 text-[8px] text-muted-foreground text-center">
                    Powered by Fixlr
                  </div>
                )}
                {isLicence && !hidePlatformBranding && (
                  <div className="border-t border-border px-3 py-2 text-[8px] text-muted-foreground text-center">
                    Powered by Fixlr
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EmailBrandingCard
        companyName={companyName}
        primaryColor={primaryColor}
        emailFooter={emailFooter}
        onEmailFooterChange={setEmailFooter}
        savedAt={savedAt}
        logoUrl={logoUrl}
      />

      <PdfBrandingCard
        companyName={companyName}
        primaryColor={primaryColor}
        logoUrl={logoUrl}
        tagline={tagline}
        footerText={footerText}
        pdfLogoUrl={pdfLogoUrl}
        uploadingPdfLogo={uploadingPdfLogo}
        onPdfLogoUpload={(file) => handleUpload(file, "pdf_logo")}
        onPdfLogoRemove={() => setPdfLogoUrl("")}
        hidePlatformBranding={isLicence && hidePlatformBranding}
        onTaglineChange={setTagline}
        onFooterTextChange={setFooterText}
        savedAt={savedAt}
      />
    </div>
  );
}
